import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  useTheme as useMuiTheme,
  alpha,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { useDebounce } from 'use-debounce';
import { ExtentionRow } from './ExtentionRow';
import AssignCosts from '../../assembliesAndItems/items/AssignCosts';
import {
  formatCurrency,
  formatNumber,
  useCalculateTotalMaterialAndLaborCosts,
} from '../closeoutHelpers';
import { useTheme } from '../../../contexts/ThemeContext';
import { useExtensionData } from '../quoteHelper';
import { ReportsDialog } from '../../reports/ReportsDialog';
import { createFlattenedRows } from './aggregationUtils';
import MaximizedTextEditor from '../../reports/MaximizedTextEditor';
import { StreamingReportEditor } from '../../reports/StreamingReportEditor';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../../stores/store';

// Import icons
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import DataArrayIcon from '@mui/icons-material/DataArray';

// Loading component to show when data is being fetched
const LoadingIndicator = () => {
  const muiTheme = useMuiTheme();
  const { darkMode } = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        height: '100%',
        flex: 1,
        backgroundColor: alpha(
          darkMode ? muiTheme.palette.background.default : muiTheme.palette.background.paper,
          0.6
        ),
      }}
    >
      <CircularProgress size={48} thickness={4} sx={{ mb: 3 }} />
      <Typography variant="h6" sx={{ mb: 1, textAlign: 'center' }}>
        Loading Extended Costs Data
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
        Please wait while we retrieve and calculate your cost data. This may take a few moments.
      </Typography>
    </Box>
  );
};

export const ExtentionHeader: React.FC<{
  refetchData?: () => Promise<any>;
}> = ({ refetchData: externalRefetch }) => {
  const muiTheme = useMuiTheme();
  const { darkMode } = useTheme();

  // Local state for search and dialog
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [extensionLaborHours, setExtensionLaborHours] = useState<number>(0);
  const [extensionMaterialCost, setExtensionMaterialCost] = useState<number>(0);
  const [isBackgroundUpdating, setIsBackgroundUpdating] = useState(false);
  const [isReportsDialogOpen, setIsReportsDialogOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [editorTitle, setEditorTitle] = useState('');
  const [streamingEditorOpen, setStreamingEditorOpen] = useState(false);
  const [streamingReportId, setStreamingReportId] = useState('');
  const [streamingReportTitle, setStreamingReportTitle] = useState('');

  // Get extension data (for estimate or change order)
  const { extensionData, refetchExtensionData, isLoading } = useExtensionData();

  // Get project and estimate/change order data for streaming reports
  const selectedProject = useStore(useUnityBuildStore).selectedProject;
  const selectedEstimate = useStore(useUnityBuildStore).selectedEstimate;

  // This is the variable that determines whether to show loading indicators in the UI
  const isDisplayLoading = isLoading && !isBackgroundUpdating;

  const calculateTotalMaterialAndLaborCosts = useCalculateTotalMaterialAndLaborCosts();

  // Create a function to handle data updates without showing loading states
  const handleSilentDataUpdate = useCallback(() => {
    // Only perform refetch if not already in progress
    if (!isBackgroundUpdating) {
      setIsBackgroundUpdating(true);

      // Use external refetch if provided, otherwise use local refetch
      const refreshFunction = externalRefetch || refetchExtensionData;

      refreshFunction()
        .then(() => {
          // Set a small delay before allowing another refresh
          setTimeout(() => {
            setIsBackgroundUpdating(false);
          }, 300);
        })
        .catch(() => {
          // Make sure we reset the flag even if there's an error
          setIsBackgroundUpdating(false);
        });
    }
  }, [isBackgroundUpdating, externalRefetch, refetchExtensionData]);

  // When new data arrives, calculate totals
  useEffect(() => {
    if (extensionData) {
      const { totalLaborHours, totalMaterialCost } =
        calculateTotalMaterialAndLaborCosts(extensionData);
      setExtensionLaborHours(totalLaborHours);
      setExtensionMaterialCost(totalMaterialCost);
    }
  }, [extensionData, calculateTotalMaterialAndLaborCosts]);

  // Search input handler
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  // Clear search handler
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  // Open/close the "AssignCosts" dialog
  const handleOpenDialog = useCallback((itemId: string) => {
    setSelectedItemId(itemId);
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setSelectedItemId('');
    handleSilentDataUpdate();
  }, [handleSilentDataUpdate]);

  // Editor handlers
  const handleOpenEditor = useCallback((title: string, content: string) => {
    setEditorTitle(title);
    setEditorContent(content);
    setEditorOpen(true);
  }, []);

  const handleEditorClose = useCallback(() => {
    setEditorOpen(false);
    setEditorContent('');
    setEditorTitle('');
  }, []);

  const handleEditorChange = useCallback((value: string) => {
    setEditorContent(value);
  }, []);

  // Streaming editor handlers
  const handleOpenStreamingEditor = useCallback((title: string, reportId: string) => {
    setStreamingReportTitle(title);
    setStreamingReportId(reportId);
    setStreamingEditorOpen(true);
  }, []);

  const handleStreamingEditorClose = useCallback(() => {
    setStreamingEditorOpen(false);
    setStreamingReportId('');
    setStreamingReportTitle('');
  }, []);

  // Filter the data based on debounced search term - memoized
  const filteredItems = useMemo(() => {
    if (!extensionData?.extendestimatesList) return [];

    const items = extensionData.extendestimatesList;

    if (!debouncedSearchTerm.trim()) {
      return items;
    }

    const searchLower = debouncedSearchTerm.toLowerCase();
    return items.filter(
      item =>
        item.itemname.toLowerCase().includes(searchLower) ||
        (item.attributegroupname && item.attributegroupname.toLowerCase().includes(searchLower))
    );
  }, [extensionData, debouncedSearchTerm]);

  // Add a state variable for grouping mode near the other state variables
  const [isBreakoutMode, setIsBreakoutMode] = useState(false);

  // Modify the groupedItems function to respect the grouping mode
  const groupedItems = useMemo(() => {
    if (isBreakoutMode) {
      // Original behavior - group by breakout ID
      return filteredItems.reduce(
        (acc, item) => {
          const key = item.breakoutid || 'No Breakout';

          if (!acc[key]) {
            acc[key] = {
              breakoutName: item.breakoutname || `Breakout ID: ${key}`,
              items: [],
            };
          }

          acc[key].items.push(item);
          return acc;
        },
        {} as Record<string, { breakoutName: string; items: typeof filteredItems }>
      );
    } else {
      // Simple mode - group only by item name, ignoring breakouts
      return {
        all: {
          breakoutName: 'All Items',
          items: filteredItems,
        },
      };
    }
  }, [filteredItems, isBreakoutMode]);

  // Use the reusable aggregation utility
  const flattenedRows = useMemo(() => {
    return createFlattenedRows(filteredItems as any, isBreakoutMode);
  }, [filteredItems, isBreakoutMode]);

  // Use a ref for the virtualized list container
  const listContainerRef = useRef<HTMLDivElement>(null);

  // Row renderer for the virtualized list
  const renderRow = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      if (!flattenedRows || index >= flattenedRows.length) {
        return null;
      }

      const row = flattenedRows[index];

      if (row.type === 'header') {
        return (
          <div
            style={{
              ...style,
              backgroundColor: darkMode
                ? alpha(muiTheme.palette.primary.main, 0.15)
                : alpha(muiTheme.palette.primary.main, 0.08),
              display: 'flex',
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 8,
              borderBottom: `1px solid ${alpha(muiTheme.palette.divider, 0.1)}`,
            }}
            key={row.key}
            data-index={index}
            data-row-key={row.key}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: muiTheme.palette.text.primary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {row.label}
            </Typography>
          </div>
        );
      } else {
        // Handle the case where row.data might be undefined
        if (!row.data) {
          return null;
        }

        // Ensure we pass the breakout context to maintain stable rendering
        const rowData = {
          ...row.data,
          // Make sure the breakout context is properly passed to child components
          _breakoutContext: row.data._breakoutContext || '',
        } as any;
        return (
          <div
            style={{
              ...style,
              width: '100%',
            }}
            key={row.key}
            data-index={index}
            data-row-key={row.key}
            data-breakout-context={rowData._breakoutContext}
            data-item-id={rowData.itemid}
          >
            <ExtentionRow
              item={rowData}
              onOpenCreateItemTab={handleOpenDialog}
              isEditingDisabled={isBreakoutMode} // Disable editing in breakout mode
            />
          </div>
        );
      }
    },
    [flattenedRows, handleOpenDialog, muiTheme, darkMode, isBreakoutMode]
  );

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      {/* Top controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexShrink: 0,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: 2,
        }}
      >
        {/* Left side controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
          {/* Search input */}
          <TextField
            size="small"
            fullWidth
            sx={{ maxWidth: 300 }}
            placeholder="Search by name or attribute..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearSearch}
                    edge="end"
                    size="small"
                    aria-label="Clear search"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            value={searchTerm}
            onChange={handleSearchChange}
            inputProps={{
              'aria-label': 'Search items by name or attribute group',
            }}
            disabled={isDisplayLoading}
          />

          {/* Add this after the search box in the left controls area: */}
          <Tooltip title={`Switch to ${isBreakoutMode ? 'Simple' : 'Breakout'} mode`} arrow>
            <span>
              <Button
                variant="outlined"
                color={isBreakoutMode ? 'secondary' : 'primary'}
                onClick={() => setIsBreakoutMode(prev => !prev)}
                size="small"
                sx={{ ml: 1 }}
                disabled={isDisplayLoading}
              >
                {isBreakoutMode ? 'Breakout Mode' : 'Simple Mode'}
              </Button>
            </span>
          </Tooltip>
        </Box>

        {/* Right side buttons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
          {/* Reports button */}
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => setIsReportsDialogOpen(true)}
            disabled={isDisplayLoading || flattenedRows.length === 0}
            size="small"
            sx={{ whiteSpace: 'nowrap', minWidth: 100 }}
            aria-label="Generate Reports"
          >
            Reports
          </Button>
        </Box>
      </Box>

      {/* Virtualized table body */}
      <Paper
        elevation={1}
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          overflow: 'hidden',
          border: `1px solid ${alpha(muiTheme.palette.divider, 0.1)}`,
          borderRadius: muiTheme.shape.borderRadius,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Show loading indicator when data is loading */}
        {isDisplayLoading ? (
          <LoadingIndicator />
        ) : (
          <>
            {/* Table headers */}
            <Box
              sx={{
                display: 'flex',
                backgroundColor: alpha(
                  darkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[100],
                  0.6
                ),
                borderBottom: `1px solid ${alpha(muiTheme.palette.divider, 0.1)}`,
                p: 1,
                pb: 0.5,
                flexShrink: 0,
              }}
            >
              {/* Name - 18% */}
              <Box
                sx={{
                  width: '18%',
                  px: 1,
                  cursor: 'pointer',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: muiTheme.palette.text.secondary }}
                >
                  Name
                </Typography>
              </Box>

              {/* Attribute Group - 10% */}
              <Box
                sx={{
                  width: '10%',
                  px: 1,
                  cursor: 'pointer',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: muiTheme.palette.text.secondary }}
                >
                  Attribute Group
                </Typography>
              </Box>

              {/* Item Qty - 8% */}
              <Box
                sx={{
                  width: '8%',
                  px: 1,
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: muiTheme.palette.text.secondary }}
                >
                  Item Qty
                </Typography>
              </Box>

              {/* Item UOM - 8% */}
              <Box
                sx={{
                  width: '8%',
                  px: 1,
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: muiTheme.palette.text.secondary }}
                >
                  Item UOM
                </Typography>
              </Box>

              {/* Item Cost Per UOM - 12% */}
              <Box
                sx={{
                  width: '12%',
                  px: 1,
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: muiTheme.palette.text.secondary }}
                >
                  Item Cost Per UOM
                </Typography>
              </Box>

              {/* Total Material Cost - 12% */}
              <Box
                sx={{
                  width: '12%',
                  px: 1,
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: muiTheme.palette.text.secondary }}
                >
                  Total Material Cost
                </Typography>
              </Box>

              {/* Hours Per Unit - 12% */}
              <Box
                sx={{
                  width: '12%',
                  px: 1,
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: muiTheme.palette.text.secondary }}
                >
                  Hours Per Unit
                </Typography>
              </Box>

              {/* Total Labor Hours - 8% */}
              <Box
                sx={{
                  width: '8%',
                  px: 1,
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: muiTheme.palette.text.secondary }}
                >
                  Total Labor Hours
                </Typography>
              </Box>

              {/* Actions - 12% */}
              <Box
                sx={{
                  width: '12%',
                  px: 1,
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: muiTheme.palette.text.secondary }}
                >
                  Actions
                </Typography>
              </Box>
            </Box>

            {flattenedRows.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  p: 4,
                  flex: 1,
                }}
              >
                <DataArrayIcon
                  sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5, mb: 2 }}
                />
                <Typography variant="body1" sx={{ mb: 1 }}>
                  No matching items found
                </Typography>
                {searchTerm && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleClearSearch}
                    startIcon={<ClearIcon />}
                  >
                    Clear Search
                  </Button>
                )}
              </Box>
            )}

            {/* Virtualized list */}
            {flattenedRows.length > 0 && (
              <Box
                ref={listContainerRef}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  scrollBehavior: 'smooth',
                  WebkitOverflowScrolling: 'touch',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    backgroundColor: alpha(muiTheme.palette.background.paper, 0.1),
                  },
                  '&::-webkit-scrollbar-thumb': {
                    borderRadius: '4px',
                    backgroundColor: alpha(muiTheme.palette.primary.main, 0.2),
                    '&:hover': {
                      backgroundColor: alpha(muiTheme.palette.primary.main, 0.3),
                    },
                  },
                }}
              >
                <AutoSizer>
                  {({ height, width }: { height: number; width: number }) => (
                    <FixedSizeList
                      height={height}
                      width={width}
                      itemCount={flattenedRows.length}
                      itemSize={56}
                    >
                      {renderRow}
                    </FixedSizeList>
                  )}
                </AutoSizer>
              </Box>
            )}

            {/* Footer with totals */}
            <Box
              sx={{
                display: 'flex',
                backgroundColor: alpha(
                  darkMode ? muiTheme.palette.grey[800] : muiTheme.palette.grey[100],
                  0.6
                ),
                borderTop: `1px solid ${alpha(muiTheme.palette.divider, 0.1)}`,
                p: 1,
                flexShrink: 0,
              }}
            >
              {/* Name column - 18% */}
              <Box sx={{ width: '18%' }}></Box>

              {/* Attribute Group column - 10% */}
              <Box sx={{ width: '10%' }}></Box>

              {/* Item Qty column - 8% */}
              <Box sx={{ width: '8%' }}></Box>

              {/* Item UOM column - 8% */}
              <Box sx={{ width: '8%' }}></Box>

              {/* Item Cost Per UOM column - 12% */}
              <Box sx={{ width: '12%' }}></Box>

              {/* Total Material Cost column - 12% */}
              <Box
                sx={{
                  width: '12%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: muiTheme.palette.text.secondary }}
                >
                  Total Material Cost
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: muiTheme.palette.success.main }}
                >
                  {formatCurrency(extensionMaterialCost)}
                </Typography>
              </Box>

              {/* Hours Per Unit column - 12% */}
              <Box sx={{ width: '12%' }}></Box>

              {/* Total Labor Hours column - 8% */}
              <Box
                sx={{
                  width: '8%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: muiTheme.palette.text.secondary }}
                >
                  Total Labor Hours
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: muiTheme.palette.info.main }}
                >
                  {formatNumber(extensionLaborHours)}
                </Typography>
              </Box>

              {/* Actions column - 12% */}
              <Box sx={{ width: '12%' }}></Box>
            </Box>
          </>
        )}
      </Paper>

      {/* Replace Dialog with direct Paper component */}
      {isDialogOpen && (
        <Paper
          elevation={5}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 'lg',
            maxHeight: '90vh',
            backgroundColor: muiTheme.palette.background.paper,
            borderRadius: 1,
            zIndex: 1300,
            // display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: `1px solid ${alpha(muiTheme.palette.divider, 0.1)}`,
            }}
          >
            <Typography variant="h6">Assign Costs</Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                color: muiTheme.palette.text.secondary,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              p: 2,
              flex: 1,
              overflowY: 'auto',
              scrollBehavior: 'smooth',
              overscrollBehavior: 'contain',
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: alpha(muiTheme.palette.background.paper, 0.1),
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '4px',
                backgroundColor: alpha(muiTheme.palette.primary.main, 0.2),
                '&:hover': {
                  backgroundColor: alpha(muiTheme.palette.primary.main, 0.3),
                },
              },
              '@media (pointer: fine)': {
                scrollbarWidth: 'thin',
                msOverflowStyle: 'auto',
              },
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <AssignCosts itemId={selectedItemId} onCostsUpdated={handleSilentDataUpdate} />
          </Box>
        </Paper>
      )}

      {/* Reports Dialog */}
      <ReportsDialog
        open={isReportsDialogOpen}
        onClose={() => setIsReportsDialogOpen(false)}
        onOpenEditor={handleOpenEditor}
        onOpenStreamingEditor={handleOpenStreamingEditor}
        projectId={selectedProject?.id}
        estimateId={selectedEstimate?.estimateid}
        estimateName={selectedEstimate?.name}
        onOpenChangeOrderReport={() => {
          // Dispatch event for change order report opening
          const event = new CustomEvent('openChangeOrderDialog', {
            detail: { reportType: 'enhanced' },
          });
          window.dispatchEvent(event);
        }}
      />

      {/* Report Editor */}
      <MaximizedTextEditor
        open={editorOpen}
        onClose={handleEditorClose}
        value={editorContent}
        onChange={handleEditorChange}
        title={editorTitle}
        placeholder="Your report content will appear here..."
        doneButtonText="Close Report"
        enableTemplates={false}
      />

      {/* Streaming Report Editor */}
      <StreamingReportEditor
        open={streamingEditorOpen}
        onClose={handleStreamingEditorClose}
        title={streamingReportTitle}
        reportId={streamingReportId}
        projectId={selectedProject?.id || ''}
        estimateId={selectedEstimate?.estimateid || ''}
        estimateName={selectedEstimate?.name || ''}
        placeholder="AI-generated report content will appear here..."
        doneButtonText="Close Report"
        enableTemplates={true}
        suggestedTemplateType="general"
      />
    </Box>
  );
};
